import { z } from 'zod';
import type { FormData } from '../types/form';

// Supported default parameters
export const formDefaultsSchema = z.object({
  // Personal information (pre-filled but NOT skipped for security/validation)
  email: z.string().email().optional(),
  phone: z.string().min(10).max(15).optional(),
  birth_date: z.string().optional(), // Format: YYYY-MM-DD or MM/DD/YYYY
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  
  // Loan defaults
  loan_purpose: z.enum(['debt_consolidation', 'credit_card_refi', 'emergency', 'home_improvement', 'large_purchases', 'other']).optional(),
  loan_amount: z.number().optional(),
  
  // Property defaults
  property_status: z.enum(['own_outright', 'own_with_mortgage', 'rent']).optional(),
  
  // Employment defaults
  employment_status: z.enum(['employed', 'employed_full_time', 'employed_part_time', 'military', 'not_employed', 'self_employed', 'retired', 'other']).optional(),
  employment_frequency: z.enum(['weekly', 'biweekly', 'twice_monthly', 'monthly']).optional(),
  
  // Income defaults
  annual_income: z.number().optional(),
  
  // Education defaults
  education_level: z.enum(['high_school', 'associate', 'bachelors', 'masters', 'doctorate', 'other_grad_degree', 'certificate', 'did_not_graduate', 'still_enrolled', 'other']).optional()
});

export type FormDefaults = z.infer<typeof formDefaultsSchema>;

// Mapping of form defaults to form steps
// Note: Personal info fields (email, phone, birth_date) are NOT in this mapping
// because we don't skip those steps - we only pre-fill them for security/validation
export const STEP_MAPPING = {
  loan_purpose: 'loan-purpose',
  loan_amount: 'loan-amount',
  property_status: 'property-status',
  employment_status: 'employment-status',
  employment_frequency: 'employment-frequency',
  annual_income: 'annual-income',
  education_level: 'education'
} as const;

// Pre-configured default values
export const PRECONFIGURED_DEFAULTS: FormDefaults = {
  loan_purpose: 'debt_consolidation',
  loan_amount: 15000,
  property_status: 'rent',
  employment_status: 'employed_full_time',
  employment_frequency: 'biweekly',
  annual_income: 80000,
  education_level: 'bachelors'
};

export class FormDefaultsService {
  private static instance: FormDefaultsService;
  private defaults: FormDefaults = {};
  private skippedSteps: Set<string> = new Set();
  private initialized: boolean = false;
  private disclaimerShown: boolean = false;
  private bulkDefaultsEnabled: boolean = false;

  private constructor() {
     this.skippedSteps.add('education');
    this.defaults.education_level = 'bachelors';
    
    // Check if disclaimer was already shown in this session
    this.disclaimerShown = sessionStorage.getItem('disclaimer_shown') === 'true';
  }

  public static getInstance(): FormDefaultsService {
    if (!FormDefaultsService.instance) {
      FormDefaultsService.instance = new FormDefaultsService();
    }
    return FormDefaultsService.instance;
  }

  /**
   * Initialize form defaults from URL parameters
   */
  public initializeFromUrl(): FormDefaults {
    if (this.initialized) {
      return this.defaults;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const detectedDefaults: Partial<FormDefaults> = {};

    // Personal information parameters (pre-fill only, don't skip steps)
    const personalInfoParams = {
      email: urlParams.get('email'),
      phone: urlParams.get('phone') || urlParams.get('phone_number'),
      birth_date: urlParams.get('birth_date') || urlParams.get('birthdate') || urlParams.get('dob') || urlParams.get('date_of_birth'),
      first_name: urlParams.get('first_name') || urlParams.get('firstname') || urlParams.get('fname'),
      last_name: urlParams.get('last_name') || urlParams.get('lastname') || urlParams.get('lname')
    };

    // Add personal info to defaults if present (but don't skip steps)
    Object.entries(personalInfoParams).forEach(([key, value]) => {
      if (value) {
        try {
          if (key === 'birth_date') {
            // Normalize date format to YYYY-MM-DD
            const normalizedDate = this.normalizeDateFormat(value);
            if (normalizedDate) {
              (detectedDefaults as any)[key] = normalizedDate;
            }
          } else if (key === 'phone') {
            // Normalize phone number to 10-digit format
            const normalizedPhone = this.normalizePhoneNumber(value);
            if (normalizedPhone) {
              (detectedDefaults as any)[key] = normalizedPhone;
            }
          } else {
            (detectedDefaults as any)[key] = value;
          }
        } catch (error) {
          console.warn(`Invalid value for ${key}:`, value, error);
        }
      }
    });

    // Check for individual parameter flags for skippable fields
    Object.keys(PRECONFIGURED_DEFAULTS).forEach(key => {
      const paramKey = key as keyof FormDefaults;
      const paramValue = urlParams.get(paramKey);
      const useDefault = urlParams.get(`use_${paramKey}`) === 'true' || urlParams.get(`default_${paramKey}`) === 'true';
      
      if (useDefault) {
        // Use the preconfigured default
        (detectedDefaults as any)[paramKey] = PRECONFIGURED_DEFAULTS[paramKey];
        // Only add to skipped steps if it's in the mapping
        if (STEP_MAPPING[paramKey as keyof typeof STEP_MAPPING]) {
          this.skippedSteps.add(STEP_MAPPING[paramKey as keyof typeof STEP_MAPPING]);
        }
      } else if (paramValue) {
        // Use the specific value provided
        try {
          if (paramKey === 'annual_income' || paramKey === 'loan_amount') {
            const numValue = parseInt(paramValue, 10);
            if (!isNaN(numValue) && numValue > 0) {
              detectedDefaults[paramKey] = numValue;
              if (STEP_MAPPING[paramKey as keyof typeof STEP_MAPPING]) {
                this.skippedSteps.add(STEP_MAPPING[paramKey as keyof typeof STEP_MAPPING]);
              }
            }
          } else {
            (detectedDefaults as Record<string, unknown>)[paramKey] = paramValue;
            if (STEP_MAPPING[paramKey as keyof typeof STEP_MAPPING]) {
              this.skippedSteps.add(STEP_MAPPING[paramKey as keyof typeof STEP_MAPPING]);
            }
          }
        } catch (error) {
          console.warn(`Invalid value for ${paramKey}:`, paramValue, error);
        }
      }
    });

    // Check for bulk default flag
    const useDefaults = urlParams.get('use_defaults') === 'true' || urlParams.get('skip_form') === 'true';
    if (useDefaults) {
      this.bulkDefaultsEnabled = true;
      Object.entries(PRECONFIGURED_DEFAULTS).forEach(([key, value]) => {
        const paramKey = key as keyof FormDefaults;
        if (!detectedDefaults[paramKey]) {
          (detectedDefaults as any)[paramKey] = value;
          if (STEP_MAPPING[paramKey as keyof typeof STEP_MAPPING]) {
            this.skippedSteps.add(STEP_MAPPING[paramKey as keyof typeof STEP_MAPPING]);
          }
        }
      });
    }

    // Validate and set defaults
    try {
      this.defaults = formDefaultsSchema.parse(detectedDefaults);
      this.initialized = true;
    } catch (error) {
      console.error('Invalid form defaults configuration:', error);
      this.defaults = {};
      this.skippedSteps.clear();
    }

    return this.defaults;
  }

  /**
   * Normalize phone number to 10-digit format (without country code)
   * Removes all non-digit characters and strips leading 1 if present
   */
  private normalizePhoneNumber(phoneNumber: string): string | null {
    try {
      // Remove all non-digit characters
      let digits = phoneNumber.replace(/\D/g, '');
      
      // Remove leading 1 if present (US country code)
      if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
      }
      
      // Validate we have exactly 10 digits
      if (digits.length !== 10) {
        console.warn(`Invalid phone number length: ${digits.length} digits`);
        return null;
      }
      
      // Return 10-digit format (the form will display it nicely)
      return digits;
    } catch (error) {
      console.warn('Error normalizing phone number:', error);
      return null;
    }
  }

  /**
   * Normalize date format from various inputs to YYYY-MM-DD
   */
  private normalizeDateFormat(dateString: string): string | null {
    // Remove any whitespace
    dateString = dateString.trim();

    // Try parsing YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // Try parsing MM/DD/YYYY format
    const mmddyyyyMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmddyyyyMatch) {
      const month = mmddyyyyMatch[1].padStart(2, '0');
      const day = mmddyyyyMatch[2].padStart(2, '0');
      const year = mmddyyyyMatch[3];
      return `${year}-${month}-${day}`;
    }

    // Try parsing M/D/YYYY format
    const mdyyyyMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdyyyyMatch) {
      const month = mdyyyyMatch[1].padStart(2, '0');
      const day = mdyyyyMatch[2].padStart(2, '0');
      const year = mdyyyyMatch[3];
      return `${year}-${month}-${day}`;
    }

    // Try parsing MM-DD-YYYY format
    const mmddyyyyDashMatch = dateString.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (mmddyyyyDashMatch) {
      const month = mmddyyyyDashMatch[1].padStart(2, '0');
      const day = mmddyyyyDashMatch[2].padStart(2, '0');
      const year = mmddyyyyDashMatch[3];
      return `${year}-${month}-${day}`;
    }

    console.warn(`Unable to parse date format: ${dateString}`);
    return null;
  }

  /**
   * Get current form defaults
   */
  public getDefaults(): FormDefaults {
    if (!this.initialized) {
      return this.initializeFromUrl();
    }
    return this.defaults;
  }

  /**
   * Check if a step should be skipped
   */
  public shouldSkipStep(stepRoute: string): boolean {
    return this.skippedSteps.has(stepRoute);
  }

  /**
   * Get all skipped steps
   */
  public getSkippedSteps(): string[] {
    return Array.from(this.skippedSteps);
  }

  /**
   * Mark a step as answered/satisfied so future skip-chain traversals
   * (e.g. re-visiting the review page and clicking Continue, or navigating
   * Back) bypass it. Needed for steps answered directly by the user
   * (such as via the review page's edit flow) rather than via
   * injectExternalDefaults.
   */
  public markStepSkipped(stepRoute: string): void {
    this.skippedSteps.add(stepRoute);
  }

  /**
   * Check if bulk defaults flag (use_defaults or skip_form) was enabled
   * This is used to redirect users to unsuccessful page when verification fails
   */
  public isBulkDefaultsEnabled(): boolean {
    return this.bulkDefaultsEnabled;
  }

  /**
   * Inject external defaults (e.g. from partner preload API) into the service.
   * Merges with any existing defaults and marks skippable steps accordingly.
   */
  public injectExternalDefaults(external: FormDefaults): void {
    if (!this.initialized) {
      this.initializeFromUrl();
    }
    this.defaults = { ...this.defaults, ...external };

    Object.keys(external).forEach(key => {
      const stepRoute = STEP_MAPPING[key as keyof typeof STEP_MAPPING];
      if (stepRoute) {
        this.skippedSteps.add(stepRoute);
      }
    });
  }

  /**
   * Apply defaults to form data
   */
  public applyToFormData(formData: Partial<FormData>): Partial<FormData> {
    const defaults = this.getDefaults();
    
    return {
      ...formData,
      // Personal information (pre-filled from URL)
      ...(defaults.email && { email: defaults.email }),
      ...(defaults.phone && { phone: defaults.phone }),
      ...(defaults.birth_date && { birthDate: defaults.birth_date }),
      ...(defaults.first_name && { firstName: defaults.first_name }),
      ...(defaults.last_name && { lastName: defaults.last_name }),
      // Loan information
      ...(defaults.loan_purpose && { loanPurpose: defaults.loan_purpose }),
      ...(defaults.loan_amount && { loanAmount: defaults.loan_amount }),
      // Property and employment
      ...(defaults.property_status && { propertyStatus: defaults.property_status }),
      ...(defaults.employment_status && { employmentStatus: defaults.employment_status }),
      ...(defaults.employment_frequency && { employmentFrequency: defaults.employment_frequency }),
      ...(defaults.annual_income && { annualIncome: defaults.annual_income }),
      // Education (always set to bachelors as default)
      educationLevel: 'bachelors'
    };
  }

  /**
   * Get analytics data for tracking
   */
  public getAnalyticsData(): Record<string, string> {
    const defaults = this.getDefaults();
    const analytics: Record<string, string> = {};

    if (Object.keys(defaults).length > 0) {
      analytics.has_form_defaults = 'true';
      analytics.skipped_steps_count = this.skippedSteps.size.toString();
      analytics.skipped_steps = Array.from(this.skippedSteps).join(',');
      
      // Track which fields were pre-filled (excluding sensitive data like actual values)
      const prefilledFields: string[] = [];
      if (defaults.email) prefilledFields.push('email');
      if (defaults.phone) prefilledFields.push('phone');
      if (defaults.birth_date) prefilledFields.push('birth_date');
      if (defaults.first_name) prefilledFields.push('first_name');
      if (defaults.last_name) prefilledFields.push('last_name');
      
      if (prefilledFields.length > 0) {
        analytics.prefilled_fields = prefilledFields.join(',');
        analytics.prefilled_fields_count = prefilledFields.length.toString();
      }
      
      // Only include non-sensitive defaults in analytics
      Object.entries(defaults).forEach(([key, value]) => {
        // Don't track actual values of personal information for privacy
        if (!['email', 'phone', 'birth_date', 'first_name', 'last_name'].includes(key)) {
          analytics[`default_${key}`] = value?.toString() || '';
        }
      });
    }

    return analytics;
  }

  /**
   * Check if disclaimer should be shown on current step
   * @param currentRoute - The route checking if it should show disclaimer
   */
  public shouldShowDisclaimer(currentRoute?: string): boolean {
    if (!this.disclaimerShown) {
      return true;
    }
    
    // If a route is provided, check if it's the one that showed the disclaimer
    if (currentRoute) {
      const shownRoute = sessionStorage.getItem('disclaimer_shown_route');
      return shownRoute === currentRoute;
    }
    
    return false;
  }

  /**
   * Mark disclaimer as shown on a specific route
   * @param route - The route where disclaimer is being shown
   */
  public markDisclaimerShown(route?: string): void {
    this.disclaimerShown = true;
    sessionStorage.setItem('disclaimer_shown', 'true');
    
    if (route) {
      sessionStorage.setItem('disclaimer_shown_route', route);
    }
  }

  /**
   * Reset the service (useful for testing)
   */
  public reset(): void {
    this.defaults = {};
    this.skippedSteps.clear();
    this.initialized = false;
    this.disclaimerShown = false;
    this.bulkDefaultsEnabled = false;
    sessionStorage.removeItem('disclaimer_shown');
    sessionStorage.removeItem('disclaimer_shown_route');
  }
}

export const formDefaultsService = FormDefaultsService.getInstance();